import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { User, hasComercialAccess } from '../types/auth'

const API_AUTH_URL = import.meta.env.VITE_API_AUTH_URL || 'https://api-auth.grupofaj.com.br'
const HUB_URL = import.meta.env.VITE_HUB_URL || 'https://hub.grupofaj.com.br'

// Função auxiliar para validar token
async function verifyTokenWithApi(token: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_AUTH_URL}/api/auth/verify`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    return response.ok
  } catch (error) {
    console.error('Erro ao verificar token:', error)
    return false
  }
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  isInitialized: boolean
}

interface AuthActions {
  initialize: () => Promise<void>
  setAuth: (user: User, token: string) => void
  logout: () => void
  verifyToken: () => Promise<boolean>
  getToken: () => string | null
  redirectToHub: () => void
}

type AuthStore = AuthState & AuthActions

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Estado inicial
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,
      isInitialized: false,

      // Inicializa a autenticação (verifica token da URL ou storage)
      initialize: async () => {
        set({ isLoading: true })

        try {
          // Verifica se tem token na URL (vindo do Hub)
          const urlParams = new URLSearchParams(window.location.search)
          const tokenFromUrl = urlParams.get('token')
          const userFromUrl = urlParams.get('user')

          if (tokenFromUrl && userFromUrl) {
            // Decodifica o usuário da URL
            const user = JSON.parse(decodeURIComponent(userFromUrl)) as User

            // Verifica se tem acesso ao módulo comercial
            if (!hasComercialAccess(user)) {
              console.error('Usuário não tem acesso ao módulo comercial')
              get().redirectToHub()
              return
            }

            // Valida o token com a API
            const isValid = await verifyTokenWithApi(tokenFromUrl)

            if (isValid) {
              set({
                user,
                token: tokenFromUrl,
                isAuthenticated: true,
                isLoading: false,
                isInitialized: true,
              })

              // Limpa a URL (remove token e user dos params)
              const cleanUrl = window.location.pathname
              window.history.replaceState({}, document.title, cleanUrl)
              return
            }
          }

          // Verifica se tem token no storage
          const state = get()
          if (state.token && state.user) {
            // Valida o token armazenado
            const isValid = await verifyTokenWithApi(state.token)

            if (isValid) {
              set({
                isAuthenticated: true,
                isLoading: false,
                isInitialized: true,
              })
              return
            }
          }

          // Sem token válido - redireciona para o Hub
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
          })

        } catch (error) {
          console.error('Erro ao inicializar autenticação:', error)
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
          })
        }
      },

      // Define autenticação
      setAuth: (user: User, token: string) => {
        set({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        })
      },

      // Logout
      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        })
        // Redireciona para o Hub
        get().redirectToHub()
      },

      // Verifica token (wrapper)
      verifyToken: async (): Promise<boolean> => {
        const token = get().token
        if (!token) return false
        return verifyTokenWithApi(token)
      },

      // Retorna o token
      getToken: (): string | null => {
        return get().token
      },

      // Redireciona para o Hub
      redirectToHub: () => {
        window.location.href = `${HUB_URL}/login`
      },
    }),
    {
      name: 'dash-comercial-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)

